import axios, { AxiosInstance } from "axios";
import { parse } from "cookie";
import Cookies from "js-cookie";
import { SESSION_KEY } from "ui/lib/constants";
import { ladderContextHeaders, ladderContextProduct } from "./ladder-context";
import {
  annotateTokenRefreshFailure,
  TokenRefreshFailureMode,
} from "./lib/rocky-api-error-handler";

let isRefreshing = false;
let refreshSubscribers: {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}[] = [];

// Last auth token this tab successfully read from the session cookie. Survives
// the cookie itself being purged by the browser mid-session (WebKit ITP's
// 7-day cap on script-written cookies) so in-flight surfaces — most painfully
// the workout journal submit — can keep authenticating with a token Rocky
// still considers valid. Module-scoped: cleared by a reload, never persisted.
let lastKnownAuthToken: string | null = null;

/**
 * Forget the in-memory auth token. Must be called from every INTENTIONAL
 * logout path (anywhere that deliberately removes SESSION_KEY) — otherwise a
 * logout that doesn't hard-reload the page (Apollo's is an SPA navigation)
 * leaves this tab silently re-authenticating via the fallback below, and a
 * post-logout 401 could even re-persist the cookie through the refresh path.
 * Browser-initiated purges (the ITP case the fallback exists for) never call
 * this, which is exactly the distinction that makes logout authoritative
 * while purge stays recoverable.
 */
export function clearLastKnownAuthToken(): void {
  lastKnownAuthToken = null;
}

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((sub) => sub.resolve(token));
  refreshSubscribers = [];
};

const onTokenRefreshFailed = (error: unknown) => {
  refreshSubscribers.forEach((sub) => sub.reject(error));
  refreshSubscribers = [];
};

// A successful refresh yields a token; a failed one carries *why* it failed so
// the interceptor can distinguish a genuinely-expired session ("rejected") from
// a transient blip ("network"). See the token-refresh taxonomy in
// `./lib/rocky-api-error-handler`.
// On success `token` is set and `failureMode` is null; on failure `token` is
// null and `failureMode` says why. A flat shape (not a discriminated union) is
// deliberate: web/apollo compile with `strictNullChecks: false`, where union
// narrowing on a discriminant is unreliable.
interface RefreshResult {
  token: string | null;
  failureMode: TokenRefreshFailureMode | null;
}

const refreshAuthToken = async (token: string): Promise<RefreshResult> => {
  const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
  // Missing config isn't an expiry — treat it as transient so we never
  // wrongly tell the user to log in again.
  if (!baseUrl) return { token: null, failureMode: "network" };

  try {
    const response = await fetch(`${baseUrl}/v1/refreshAuthToken`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });

    // Rocky explicitly refused the session: genuinely expired.
    if (response.status === 401 || response.status === 403) {
      return { token: null, failureMode: "rejected" };
    }
    // Any other non-2xx (5xx/429/...) means the refresh call itself failed —
    // no verdict on the session, so treat it as transient, never as expiry.
    if (!response.ok) return { token: null, failureMode: "network" };

    const data = await response.json();
    const newToken = data?.authToken;
    // An ok response with no token means Rocky won't renew this session either.
    if (!newToken) return { token: null, failureMode: "rejected" };

    Cookies.set(SESSION_KEY, newToken, { expires: 365 });
    lastKnownAuthToken = newToken;
    return { token: newToken, failureMode: null };
  } catch {
    // The refresh never reached a verdict (offline/timeout): may still be valid.
    return { token: null, failureMode: "network" };
  }
};

const attachAuthInterceptors = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      let token;

      if (typeof window !== "undefined") {
        // client-side: read cookies from document.cookie
        token = Cookies.get(SESSION_KEY);
        if (token) {
          lastKnownAuthToken = token;
        } else if (lastKnownAuthToken) {
          // The session cookie can vanish while a tab is open: WebKit's ITP
          // caps script-written cookies at 7 days on every iOS browser, and
          // the purge lands mid-session (LADDER-WEBSITE-8JN: a user finished
          // a workout, idled 39 minutes on the finish screen, and every
          // subsequent Rocky call went out with no Authorization header —
          // the token itself was still weeks from expiry). Fall back to the
          // token this tab last used so a browser-purged cookie doesn't
          // strand a session Rocky still accepts. We deliberately do NOT
          // rewrite the cookie here — if Rocky rejects this token the normal
          // 401 path runs, and a cross-tab logout isn't durably resurrected.
          token = lastKnownAuthToken;
        }
      } else if (config.headers.cookie) {
        // server-side: read cookies from headers
        const cookies = parse(config.headers.cookie);
        token = cookies[SESSION_KEY];
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: refresh token on 401 and retry
  if (typeof window !== "undefined") {
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        // Prefer the live cookie, but fall back to the tab's last-known token
        // when the cookie has been purged (see lastKnownAuthToken above) —
        // a successful refresh below re-persists a fresh cookie, healing the
        // jar via a real, Rocky-validated auth roundtrip.
        const currentToken = Cookies.get(SESSION_KEY) ?? lastKnownAuthToken;
        if (!currentToken) {
          // No cookie AND no in-memory token: nothing to refresh with. Tag the
          // mode so this previously-invisible branch is distinguishable in
          // Sentry from a refresh Rocky rejected (LADDER-WEBSITE-8JN — this
          // exact silent bail was why journal-save 401s looked inexplicable).
          annotateTokenRefreshFailure(error, "missing");
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // Another request is already refreshing - wait for it
          return new Promise((resolve, reject) => {
            refreshSubscribers.push({
              resolve: (newToken) => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                resolve(axiosInstance(originalRequest));
              },
              reject,
            });
          });
        }

        isRefreshing = true;
        const { token: newToken, failureMode } =
          await refreshAuthToken(currentToken);
        isRefreshing = false;

        if (!newToken) {
          // Additively tag the original 401 with the refresh failure mode so
          // downstream consumers can tell a real expiry from a transient blip.
          // Control flow is unchanged — we still reject the original error.
          annotateTokenRefreshFailure(error, failureMode ?? "network");
          onTokenRefreshFailed(error);
          return Promise.reject(error);
        }

        onTokenRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      }
    );
  }
};

// Identifies the consuming app on Rocky access logs (#2214). queries is
// shared, so product comes from per-app build-time env, not a literal here.
const ladderContext = ladderContextHeaders({
  product: ladderContextProduct(),
  component: "queriesApiClient",
});

// Same-origin Next.js routes; a fixed baseURL turned every preview deploy
// into a cross-origin call to the dev alias.
const localApiClient = axios.create({
  headers: {
    "Content-Type": "application/json",
    ...ladderContext,
  },
});

const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    ...ladderContext,
  },
});

// Request interceptor for adding auth token from cookie
attachAuthInterceptors(apiClient);
attachAuthInterceptors(localApiClient);

export { apiClient, localApiClient };
